import { Card, Group, Text, Progress, Stack, Badge, Tooltip, rem } from '@mantine/core'
import { IconCpu, IconDatabase } from '@tabler/icons-react'
import { formatBytes } from '@/utils/format'
import type { GpuDeviceInfo } from '@/types'

interface GpuAllocationChartProps {
  device: GpuDeviceInfo
  nodeId: string
}

export function GpuAllocationChart({ device, nodeId }: GpuAllocationChartProps) {
  const totalAllocatedMemory = device.allocations.reduce(
    (sum, alloc) => sum + alloc.memory_allocated_mb,
    0
  )
  const memoryUtilization = (totalAllocatedMemory / device.memory_mb) * 100
  const isAvailable = device.allocations.length === 0

  // Calculate compute allocation
  const totalComputeFraction = device.allocations.reduce(
    (sum, alloc) => sum + alloc.compute_fraction,
    0
  )
  const computeUtilization = totalComputeFraction * 100

  // Determine status color
  const getStatusColor = () => {
    if (isAvailable) return 'green'
    if (device.allocations.some(a => a.exclusive)) return 'red'
    if (device.allocations.some(a => a.mig_instance)) return 'blue'
    return 'orange'
  }

  const getStatusLabel = () => {
    if (isAvailable) return 'Available'
    if (device.allocations.some(a => a.exclusive)) return 'Exclusive'
    if (device.allocations.some(a => a.mig_instance)) return 'MIG Partitioned'
    return 'Time-Sliced'
  }

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Text fw={500} size="lg">{device.name}</Text>
            <Text size="xs" c="dimmed">
              GPU {device.index} | {device.pci_bus_id}
            </Text>
          </div>
          <Badge color={getStatusColor()} variant="light">
            {getStatusLabel()}
          </Badge>
        </Group>

        {/* GPU Specs */}
        <Group gap="xl">
          <Tooltip label="CUDA Cores">
            <Group gap="xs">
              <IconCpu style={{ width: rem(16), height: rem(16) }} />
              <Text size="sm">{device.cuda_cores.toLocaleString()}</Text>
            </Group>
          </Tooltip>

          {device.tensor_cores && (
            <Tooltip label="Tensor Cores">
              <Group gap="xs">
                <IconCpu style={{ width: rem(16), height: rem(16) }} />
                <Text size="sm">{device.tensor_cores.toLocaleString()} TC</Text>
              </Group>
            </Tooltip>
          )}

          <Tooltip label="Total Memory">
            <Group gap="xs">
              <IconDatabase style={{ width: rem(16), height: rem(16) }} />
              <Text size="sm">{formatBytes(device.memory_mb * 1024 * 1024)}</Text>
            </Group>
          </Tooltip>

          <Text size="sm" c="dimmed">
            CC {device.compute_capability}
          </Text>
        </Group>

        {/* Memory Utilization */}
        <div>
          <Group justify="space-between" mb="xs">
            <Text size="sm" fw={500}>Memory Utilization</Text>
            <Text size="sm" c="dimmed">
              {formatBytes(totalAllocatedMemory * 1024 * 1024)} / {formatBytes(device.memory_mb * 1024 * 1024)}
            </Text>
          </Group>
          <Progress
            value={memoryUtilization}
            color={memoryUtilization > 90 ? 'red' : memoryUtilization > 70 ? 'orange' : 'blue'}
            size="lg"
          />
          <Text size="xs" c="dimmed" mt="xs">
            {memoryUtilization.toFixed(1)}% allocated
          </Text>
        </div>

        {/* Compute Utilization */}
        <div>
          <Group justify="space-between" mb="xs">
            <Text size="sm" fw={500}>Compute Allocation</Text>
            <Text size="sm" c="dimmed">
              {(totalComputeFraction * 100).toFixed(0)}%
            </Text>
          </Group>
          <Progress
            value={computeUtilization}
            color={computeUtilization > 90 ? 'red' : computeUtilization > 70 ? 'orange' : 'green'}
            size="lg"
          />
        </div>

        {/* Allocations */}
        {device.allocations.length > 0 && (
          <div>
            <Text size="sm" fw={500} mb="xs">
              Active Allocations ({device.allocations.length})
            </Text>
            <Stack gap="xs">
              {device.allocations.map((alloc, idx) => (
                <Card key={idx} padding="xs" withBorder>
                  <Group justify="space-between">
                    <div>
                      <Text size="xs" fw={500}>Task: {alloc.task_id.substring(0, 12)}</Text>
                      {alloc.mig_instance && (
                        <Text size="xs" c="dimmed">MIG: {alloc.mig_instance}</Text>
                      )}
                    </div>
                    <Group gap="xs">
                      <Badge size="xs" variant="light">
                        {formatBytes(alloc.memory_allocated_mb * 1024 * 1024)}
                      </Badge>
                      <Badge size="xs" variant="light" color={alloc.exclusive ? 'red' : 'blue'}>
                        {alloc.exclusive ? 'Exclusive' : `${(alloc.compute_fraction * 100).toFixed(0)}%`}
                      </Badge>
                    </Group>
                  </Group>
                </Card>
              ))}
            </Stack>
          </div>
        )}

        {/* Driver Info */}
        <Group justify="space-between">
          <Text size="xs" c="dimmed">Driver: {device.driver_version}</Text>
          <Text size="xs" c="dimmed">CUDA: {device.cuda_version}</Text>
        </Group>
      </Stack>
    </Card>
  )
}
